(() => {
  const initialized = new WeakMap();

  function createHomeGalleryCard(design) {
    const article = document.createElement("article");
    article.className = "aucelia-guide-gallery__card";
    article.setAttribute("role", "listitem");
    article.dataset.designId = design.id;

    const link = document.createElement("a");
    link.className = "aucelia-guide-gallery__card-link";
    link.href = design.customizeUrl;
    link.setAttribute("aria-label", `Customize ${design.name}, ${design.price}`);

    const media = document.createElement("span");
    media.className = "aucelia-guide-gallery__media";
    const image = document.createElement("img");
    image.dataset.src = design.imageUrl;
    image.alt = design.imageAlt || design.name;
    image.width = 1200;
    image.height = 1200;
    image.loading = "lazy";
    image.decoding = "async";
    media.append(image);

    const copy = document.createElement("span");
    copy.className = "aucelia-guide-gallery__card-copy";
    const heading = document.createElement("span");
    heading.className = "aucelia-guide-gallery__card-heading";
    const title = document.createElement("strong");
    title.textContent = design.name;
    const price = document.createElement("span");
    price.textContent = design.price;
    heading.append(title, price);

    const materials = document.createElement("span");
    materials.className = "aucelia-guide-gallery__materials";
    materials.textContent = design.materials;

    const action = document.createElement("span");
    action.className = "aucelia-guide-gallery__card-action";
    const actionLabel = document.createElement("span");
    actionLabel.textContent = "CUSTOMIZE THIS DESIGN";
    const arrow = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    arrow.setAttribute("viewBox", "0 0 24 24");
    arrow.setAttribute("aria-hidden", "true");
    arrow.setAttribute("focusable", "false");
    const arrowPath = document.createElementNS("http://www.w3.org/2000/svg", "path");
    arrowPath.setAttribute("d", "M5 12h14M14 7l5 5-5 5");
    arrow.append(arrowPath);
    action.append(actionLabel, arrow);

    copy.append(heading, materials, action);
    link.append(media, copy);
    article.append(link);
    article.setAttribute("aria-hidden", "true");
    link.tabIndex = -1;
    return article;
  }

  function hydrateGalleryImage(card, priority = false) {
    const image = card?.querySelector("img[data-src]");
    const source = image?.dataset.src;
    if (!image || !source) return;
    image.fetchPriority = priority ? "high" : "low";
    image.src = source;
    delete image.dataset.src;
  }

  function hydrateHomeGallery(root) {
    const rail = root.querySelector("[data-aucelia-home-gallery-rail]");
    const designs = Array.isArray(window.__AUCELIA_HOME_GALLERY_DESIGNS__)
      ? window.__AUCELIA_HOME_GALLERY_DESIGNS__
      : [];
    const uniqueIds = new Set(designs.map((design) => design.id));

    if (
      !rail ||
      designs.length !== 105 ||
      uniqueIds.size !== 105 ||
      designs[0]?.id !== "color-crystal-clear"
    ) {
      return;
    }

    const pinnedCard = rail.querySelector(".aucelia-guide-gallery__card");
    if (!pinnedCard) return;
    pinnedCard.dataset.designId = designs[0].id;

    const remainingCards = document.createDocumentFragment();
    designs.slice(1).forEach((design) => {
      remainingCards.append(createHomeGalleryCard(design));
    });

    pinnedCard.remove();
    rail.replaceChildren(pinnedCard, remainingCards);
    rail.setAttribute("aria-label", "105 Aucelia bracelet designs");
  }

  function initialize(root) {
    if (!(root instanceof HTMLElement) || initialized.has(root)) return;

    const cleanup = [];
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    hydrateHomeGallery(root);
    const galleryRail = root.querySelector("[data-aucelia-home-gallery-rail]");
    const galleryCards = galleryRail ? [...galleryRail.querySelectorAll(".aucelia-guide-gallery__card")] : [];
    const galleryProgress = root.querySelector("[data-aucelia-home-gallery-progress]");
    let galleryIndex = 0;
    let visibleGalleryIndexes = new Set();

    const galleryStep = () => galleryCards.length > 1
      ? galleryCards[1].offsetLeft - galleryCards[0].offsetLeft
      : 0;
    const galleryVisibleCount = () => {
      const step = galleryStep();
      return step > 0 && galleryRail
        ? Math.max(1, Math.min(galleryCards.length, Math.round(galleryRail.clientWidth / step)))
        : 1;
    };
    const updateGalleryProgress = () => {
      if (galleryProgress) {
        galleryProgress.textContent = `${String(galleryIndex + 1).padStart(2, "0")} / ${String(galleryCards.length).padStart(2, "0")}`;
      }
      const visibleCount = galleryVisibleCount();
      const nextVisibleIndexes = new Set();
      const visibleEnd = Math.min(galleryCards.length, galleryIndex + visibleCount);

      for (let index = galleryIndex; index < visibleEnd; index += 1) {
        nextVisibleIndexes.add(index);
      }

      visibleGalleryIndexes.forEach((index) => {
        if (nextVisibleIndexes.has(index)) return;
        const card = galleryCards[index];
        card?.setAttribute("aria-hidden", "true");
        const link = card?.querySelector("a");
        if (link) link.tabIndex = -1;
      });

      nextVisibleIndexes.forEach((index) => {
        const card = galleryCards[index];
        card?.setAttribute("aria-hidden", "false");
        const link = card?.querySelector("a");
        if (link) link.tabIndex = 0;
        hydrateGalleryImage(card, true);
      });

      const preloadStart = Math.max(0, galleryIndex - 1);
      const preloadEnd = Math.min(galleryCards.length, visibleEnd + 3);
      for (let index = preloadStart; index < preloadEnd; index += 1) {
        hydrateGalleryImage(galleryCards[index]);
      }

      visibleGalleryIndexes = nextVisibleIndexes;
    };
    const moveGallery = (index, behavior = "smooth") => {
      if (!galleryRail || galleryCards.length < 2) return;
      const maximumIndex = Math.max(0, galleryCards.length - galleryVisibleCount());
      galleryIndex = Math.max(0, Math.min(index, maximumIndex));
      galleryRail.scrollTo({
        left: galleryCards[galleryIndex].offsetLeft - galleryCards[0].offsetLeft,
        behavior,
      });
      updateGalleryProgress();
    };

    if (galleryRail && galleryCards.length > 1) {
      if ("IntersectionObserver" in window) {
        const galleryImageObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            hydrateGalleryImage(entry.target);
            galleryImageObserver.unobserve(entry.target);
          });
        }, {
          root: galleryRail,
          rootMargin: "0px 180% 0px 180%",
          threshold: 0.01,
        });
        galleryCards.forEach((card) => galleryImageObserver.observe(card));
        cleanup.push(() => galleryImageObserver.disconnect());
      }

      let scrollFrame = 0;
      const onScroll = () => {
        window.cancelAnimationFrame(scrollFrame);
        scrollFrame = window.requestAnimationFrame(() => {
          const step = galleryStep();
          if (step <= 0) return;
          const maximumIndex = Math.max(0, galleryCards.length - galleryVisibleCount());
          galleryIndex = Math.max(0, Math.min(Math.round(galleryRail.scrollLeft / step), maximumIndex));
          updateGalleryProgress();
        });
      };
      galleryRail.addEventListener("scroll", onScroll, { passive: true });
      cleanup.push(() => {
        galleryRail.removeEventListener("scroll", onScroll);
        window.cancelAnimationFrame(scrollFrame);
      });

      let galleryIsNearViewport = !("IntersectionObserver" in window);
      if ("IntersectionObserver" in window) {
        const galleryActivityObserver = new IntersectionObserver(([entry]) => {
          galleryIsNearViewport = Boolean(entry?.isIntersecting);
        }, { rootMargin: "240px 0px" });
        galleryActivityObserver.observe(galleryRail);
        cleanup.push(() => galleryActivityObserver.disconnect());
      }

      if (!reduceMotion) {
        const galleryTimer = window.setInterval(() => {
          if (!galleryIsNearViewport || document.hidden) return;
          const maximumIndex = Math.max(0, galleryCards.length - galleryVisibleCount());
          moveGallery(galleryIndex >= maximumIndex ? 0 : galleryIndex + 1);
        }, 2500);
        cleanup.push(() => window.clearInterval(galleryTimer));
      }
      updateGalleryProgress();
    }

    const processImageCard = root.querySelector("[data-aucelia-process-image-card]");
    const processImage = root.querySelector("[data-aucelia-process-image]");
    const processStatus = root.querySelector("[data-aucelia-process-status]");
    const processSources = [...root.querySelectorAll("[data-aucelia-process-source]")].map((source) => ({
      image: source.dataset.image,
      alt: source.dataset.alt,
    })).filter((source) => source.image);

    if (processImageCard && processImage && processSources.length > 1 && !reduceMotion) {
      let imageIndex = 0;
      let transitionTimer = 0;
      let processIsNearViewport = !("IntersectionObserver" in window);
      let preparedProcessIndex = -1;

      const prepareNextProcessImage = () => {
        const nextIndex = (imageIndex + 1) % processSources.length;
        if (preparedProcessIndex === nextIndex) return;
        const preload = new Image();
        preload.decoding = "async";
        preload.src = processSources[nextIndex].image;
        preparedProcessIndex = nextIndex;
      };

      if ("IntersectionObserver" in window) {
        const processObserver = new IntersectionObserver(([entry]) => {
          processIsNearViewport = Boolean(entry?.isIntersecting);
          if (processIsNearViewport) prepareNextProcessImage();
        }, { rootMargin: "320px 0px" });
        processObserver.observe(processImageCard);
        cleanup.push(() => processObserver.disconnect());
      }

      const processTimer = window.setInterval(() => {
        if (!processIsNearViewport || document.hidden) return;
        processImageCard.classList.add("is-changing");
        transitionTimer = window.setTimeout(() => {
          imageIndex = (imageIndex + 1) % processSources.length;
          processImage.src = processSources[imageIndex].image;
          processImage.alt = processSources[imageIndex].alt || "Aucelia bracelet design";
          if (processStatus) {
            processStatus.textContent = `Bracelet example ${imageIndex + 1} of ${processSources.length}`;
          }
          processImageCard.classList.remove("is-changing");
          preparedProcessIndex = -1;
          prepareNextProcessImage();
        }, 170);
      }, 2600);
      cleanup.push(() => {
        window.clearInterval(processTimer);
        window.clearTimeout(transitionTimer);
      });
    }

    const scrollToHash = () => {
      if (!window.location.hash) return;
      const target = document.getElementById(window.location.hash.slice(1));
      if (!target || !root.contains(target)) return;
      const priorBehavior = document.documentElement.style.scrollBehavior;
      document.documentElement.style.scrollBehavior = "auto";
      window.scrollTo({ top: Math.max(0, target.getBoundingClientRect().top + window.scrollY - 82) });
      document.documentElement.style.scrollBehavior = priorBehavior;
    };
    window.requestAnimationFrame(scrollToHash);
    const hashTimer = window.setTimeout(scrollToHash, 700);
    cleanup.push(() => window.clearTimeout(hashTimer));

    initialized.set(root, cleanup);
  }

  function initializeAll(container = document) {
    if (container instanceof HTMLElement && container.matches("[data-aucelia-homepage-v4]")) initialize(container);
    container.querySelectorAll?.("[data-aucelia-homepage-v4]").forEach(initialize);
  }

  function destroy(container) {
    const roots = container instanceof HTMLElement && container.matches("[data-aucelia-homepage-v4]")
      ? [container]
      : [...(container.querySelectorAll?.("[data-aucelia-homepage-v4]") || [])];
    roots.forEach((root) => {
      (initialized.get(root) || []).forEach((cleanup) => cleanup());
      initialized.delete(root);
    });
  }

  initializeAll();
  document.addEventListener("shopify:section:load", (event) => initializeAll(event.target));
  document.addEventListener("shopify:section:unload", (event) => destroy(event.target));
})();
